// MarkdownRenderer.js - Render markdown dengan tabel, code, list, heading + chart blocks
import React, { useState } from 'react';
import { Copy, Check, Download } from 'lucide-react';
import AIChartRenderer, { parseChartSpec, isChartSpec } from './AIChartRenderer';
import { openNoteRef } from './noteNav';

// ── [C1] Unduh isi code block sebagai berkas ─────────────────────────────────
// Model sering menghasilkan artefak utuh (report ABAP, skrip PS1, DDL SQL) yang
// ingin disimpan user, bukan sekadar dibaca. Unduhan dilakukan MURNI di browser
// lewat Blob — tidak ada berkas yang menyentuh disk server, jadi tidak ada
// permukaan serangan baru dan tidak perlu housekeeping/kuota.
//
// Nama berkas boleh ditentukan model lewat info-string fence, mis.
//   ```abap:ZCL_ROLE_EXPORT.abap
// Nama dari model = teks tak tepercaya, jadi tetap disanitasi di sini.
const LANG_EXT = {
  abap: 'abap', sql: 'sql', js: 'js', javascript: 'js', jsx: 'jsx',
  ts: 'ts', typescript: 'ts', tsx: 'tsx', json: 'json', md: 'md',
  markdown: 'md', ps1: 'ps1', powershell: 'ps1', py: 'py', python: 'py',
  sh: 'sh', bash: 'sh', shell: 'sh', yaml: 'yml', yml: 'yml',
  xml: 'xml', html: 'html', css: 'css', csv: 'csv', java: 'java',
  ini: 'ini', conf: 'conf', env: 'txt', text: 'txt', txt: 'txt',
};

// Buang path separator, kutip, dan karakter aneh. Cegah juga nama kosong,
// nama yang hanya titik, dan nama kelewat panjang.
function sanitizeFileName(raw, fallbackExt) {
  let n = String(raw || '')
    .replace(/[\\/]/g, '')            // path traversal / subfolder
    .replace(/["'`]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9._-]/g, '')
    .replace(/^\.+/, '')              // jangan buat berkas tersembunyi
    .slice(0, 100);
  if (!n) return '';
  // Ekstensi wajib ada dan harus dari daftar yang dikenal. Nama seperti
  // "laporan.exe" akan berakhir "laporan.exe.txt" — sengaja, bukan bug.
  const ext = (n.split('.').pop() || '').toLowerCase();
  const known = Object.values(LANG_EXT).includes(ext);
  if (!known || !n.includes('.')) n += '.' + (fallbackExt || 'txt');
  return n;
}

function CodeBlock({ code, lang, fileName, index }) {
  const [copied, setCopied]         = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const ext = LANG_EXT[lang] || 'txt';
  const finalName = sanitizeFileName(fileName, ext) ||
    `snippet-${index || 1}.${ext}`;

  const download = () => {
    try {
      const blob = new Blob([code], { type: 'text/plain;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // revoke ditunda: sebagian browser membatalkan unduhan bila URL dicabut
      // tepat setelah click().
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2000);
    } catch (e) {
      console.warn('[CodeBlock] gagal mengunduh:', e.message);
    }
  };

  return (
    <div className="my-2 rounded-xl overflow-hidden border border-slate-200">
      {/* [KODE-TERANG] Blok kode bertema TERANG, selaras dengan dashboard.
          Sebelumnya slate-900, lalu slate-800; keduanya masih terbaca "hitam",
          jadi pendekatan menurunkan tingkat gelap ditinggalkan sama sekali.

          Badan slate-50 + teks slate-800  ≈ 13,6:1
          Header slate-100 + teks slate-600 ≈ 7,5:1
          Keduanya jauh di atas ambang AA; ini bukan kebetulan, kombinasi terang
          mudah gagal kontras kalau teksnya dibiarkan slate-400/500 seperti versi
          gelap. Jangan turunkan teks header di bawah slate-600.

          Header diberi border-b: pada tema gelap perbedaan 700 vs 800 sudah cukup
          memisahkan bilah dari badan, tapi slate-100 vs slate-50 hanya berselisih
          tipis (#f1f5f9 vs #f8fafc) dan tanpa garis keduanya menyatu.

          ⚠ ExportPDF.js punya salinan warnanya sendiri (style inline), karena PDF
          dibangun dari string HTML lepas yang tidak pernah melewati build Tailwind.
          Nilainya SENGAJA TIDAK identik: halaman PDF sudah berlatar #f8fafc
          (ExportPDF.js body), jadi blok kode di sana memakai #fff — memakai
          slate-50 yang sama akan membuatnya lenyap ke dalam halaman. Yang harus
          dijaga adalah kesamaan TAMPILAN, bukan kesamaan angka. Ubah di sini,
          periksa di sana. */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-100 border-b border-slate-200">
        <span className="text-xs text-slate-600 font-mono truncate" title={finalName}>
          {fileName ? finalName : (lang || 'code')}
        </span>
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={download} title={`Unduh sebagai ${finalName}`}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors">
            {downloaded ? <Check style={{width:11,height:11}}/> : <Download style={{width:11,height:11}}/>}
            {downloaded ? 'Saved' : 'Download'}
          </button>
          <button onClick={copy} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors">
            {copied ? <Check style={{width:11,height:11}}/> : <Copy style={{width:11,height:11}}/>}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <pre className="bg-slate-50 px-4 py-3 overflow-x-auto text-xs text-slate-800 leading-relaxed font-mono whitespace-pre">
        {code}
      </pre>
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
// `animate` → baris data yang BARU muncul saat streaming di-fade-in satu per satu.
// Key <tr> = indeks baris (append-only saat streaming) → baris lama tak replay.
function MarkdownTable({ rows, animate }) {
  if (rows.length < 2) return null;
  const headers  = rows[0];
  const dividers = rows[1];
  // Cek apakah baris kedua adalah divider (---|---|---)
  const isDivider = dividers.every(c => /^[-:]+$/.test(c.trim()));
  const dataRows  = isDivider ? rows.slice(2) : rows.slice(1);

  // Alignment dari divider
  const aligns = isDivider ? dividers.map(d => {
    const t = d.trim();
    if (t.startsWith(':') && t.endsWith(':')) return 'center';
    if (t.endsWith(':')) return 'right';
    return 'left';
  }) : headers.map(() => 'left');

  return (
    <div className="my-3 overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
      <table className="w-full text-xs min-w-max">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {headers.map((h, i) => (
              <th key={i} style={{ textAlign: aligns[i] }}
                className="px-4 py-2.5 font-semibold text-slate-700 uppercase tracking-wider text-xs whitespace-nowrap">
                {renderInline(h.trim())}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} className={`${ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}${animate ? ' ai-rv-row' : ''}`}>
              {headers.map((_, ci) => (
                <td key={ci} style={{ textAlign: aligns[ci] }}
                  className="px-4 py-2 text-slate-700 border-b border-slate-100 last:border-0">
                  {renderInline((row[ci] || '').trim())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Inline renderer (bold, italic, code, link) ────────────────────────────────
// Sumber untuk sitasi [n] — di-set saat MarkdownRenderer dirender (sinkron).
let _citeSources = [];
function openCitation(n) {
  const s = _citeSources[n - 1];
  if (!s) return;
  // Dokumen SolMan bukan note → buka viewer di halaman Solman Documents (tab baru).
  if (s.kb_source === 'solman_docs' && /^[0-9A-Fa-f]{32}$/.test(String(s.doc_id || ''))) {
    window.open('/dashboard/solman-docs?view=' + String(s.doc_id).toUpperCase(), '_blank', 'noopener');
    return;
  }
  openNoteRef(s);
}

function renderInline(text) {
  if (!text) return null;
  // Split by bold, italic, inline code, dan sitasi [n] agar bisa diklik.
  const parts = text.split(/(\[[^\]\n]+\]\(\/[^)\s]*\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[\d+\])/g);
  return parts.map((part, i) => {
    // Link markdown relatif (hanya path internal '/...') → buka di tab baru.
    const lk = /^\[([^\]\n]+)\]\((\/[^)\s]*)\)$/.exec(part);
    if (lk) return <a key={i} href={lk[2]} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-800 underline">{lk[1]}</a>;
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} className="font-semibold text-slate-800">{part.slice(2,-2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
      return <em key={i} className="italic">{part.slice(1,-1)}</em>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="bg-slate-100 text-brand-700 px-1 py-0.5 rounded text-xs font-mono">{part.slice(1,-1)}</code>;
    // Sitasi [n] → tombol ke sumber bila ada datanya.
    const m = /^\[(\d+)\]$/.exec(part);
    if (m) {
      const n = parseInt(m[1], 10);
      const s = _citeSources[n - 1];
      if (s) {
        return (
          <button
            key={i}
            onClick={() => openCitation(n)}
            title={`Buka sumber: ${s.title || '(tanpa judul)'}`}
            className="inline-flex items-center align-baseline text-brand-600 hover:text-brand-800 hover:bg-brand-50 font-medium cursor-pointer rounded px-0.5"
          >
            {part}
          </button>
        );
      }
      return part; // tak ada data sumber utk nomor ini → biarkan teks biasa
    }
    return part;
  });
}

// ── Parse table rows dari markdown ───────────────────────────────────────────
function parseTableRows(lines) {
  return lines.map(line =>
    line.replace(/^\||\|$/g, '').split('|').map(c => c.trim())
  );
}

// ── Main Markdown Parser ──────────────────────────────────────────────────────
// `animate` (opsional) → tiap blok yang BARU mount saat streaming diberi kelas
// `ai-rv` (fade+rise). KUNCI blok memakai indeks BARIS-AWAL blok, bukan baris
// akhir. Saat streaming teks hanya bertambah (append-only), sehingga baris-awal
// blok lama tetap konstan → React me-reuse DOM-nya → animasi tak replay; hanya
// blok baru yang mount & ter-animasi. Inilah yang bikin stream terasa halus.
export function MarkdownRenderer({ text, sources, animate = false }) {
  if (!text) return null;
  _citeSources = Array.isArray(sources) ? sources : [];

  const rv       = animate ? ' ai-rv' : '';   // kelas reveal (kalau streaming)
  const lines    = text.split('\n');
  const elements = [];
  let i = 0;
  let codeSeq = 0;                 // [C1] nomor urut blok kode → nama fallback

  while (i < lines.length) {
    const start = i;                 // baris-awal blok → dasar key yang stabil
    const line  = lines[i];

    // Code block (```)
    if (line.startsWith('```')) {
      // [C1] Info-string boleh membawa nama berkas: ```abap:ZCL_FOO.abap
      // `lang` tetap di-lowercase (deteksi chart di bawah bergantung padanya),
      // TAPI nama berkas dipertahankan huruf besar-kecilnya — nama objek ABAP
      // dan skrip PowerShell lazim ditulis kapital.
      const info    = line.slice(3).trim();
      const sepAt   = info.indexOf(':');
      const lang    = (sepAt >= 0 ? info.slice(0, sepAt) : info).trim().toLowerCase();
      const fileHint = sepAt >= 0 ? info.slice(sepAt + 1).trim() : '';
      const codeLines = [];
      i++;
      // Deteksi fence penutup. Kasus normal: baris TERSENDIRI diawali ```.
      // Kasus artefak model (terutama Groq/Llama saat streaming): fence
      // penutup MENEMPEL di akhir baris konten terakhir tanpa baris baru,
      // mis. `...}]}` + tiga backtick pada baris yang sama. Tanpa penanganan
      // ini, closing fence tidak pernah "ketemu" (karena baris itu tidak
      // DIAWALI ```) sehingga seluruh sisa pesan ikut tertelan sebagai kode —
      // dan untuk blok ABAP/SQL/PS1, backtick sisa itu ikut terunduh sebagai
      // bagian file, gagal aktivasi/eksekusi tanpa peringatan apa pun.
      //
      // Fence tengah-baris HANYA dianggap penutup bila sisa baris setelah ```
      // kosong atau berupa token pendek alfanumerik (mis. sisa label bahasa
      // salah ketik) — BUKAN teks bebas. Ini mencegah salah tembak pada
      // konten yang memang berisi literal ``` diikuti teks lain (mis. contoh
      // markdown-di-dalam-markdown).
      while (i < lines.length) {
        const cur = lines[i];
        const fenceIdx = cur.indexOf('```');
        if (fenceIdx !== -1) {
          const after = cur.slice(fenceIdx + 3);
          if (fenceIdx === 0 || /^[A-Za-z0-9_-]*$/.test(after)) {
            const before = cur.slice(0, fenceIdx);
            if (before !== '') codeLines.push(before);
            break;
          }
        }
        codeLines.push(cur);
        i++;
      }
      const rawCode = codeLines.join('\n');

      // ── Chart block — render sebagai visual chart ────────────────────────
      // [A] Toleran: selain ```chart, kenali juga ```json / fence tanpa label
      // yang ISI-nya jelas spec chart (punya type chart valid + data/series).
      const maybeChartLang = (lang === 'chart' || lang === 'json' || lang === '');
      const spec = maybeChartLang ? parseChartSpec(rawCode) : null;
      if (spec && isChartSpec(spec)) {
        elements.push(<div key={"chart@"+start} className={rv.trim() || undefined}><AIChartRenderer spec={spec} /></div>);
      } else if (lang === 'chart') {
        // ```chart tapi JSON invalid → tampilkan peringatan + kode mentah
        elements.push(
          <div key={"charterr@"+start} className={rv.trim() || undefined}>
            <div style={{ padding: '4px 8px', background: '#FEF2F2', border: '1px solid #FCA5A5',
              borderRadius: '8px 8px 0 0', fontSize: 11, color: '#B91C1C', fontWeight: 600 }}>
              ⚠️ Chart spec JSON tidak valid
            </div>
            <CodeBlock code={rawCode} lang="json" fileName={fileHint} index={++codeSeq} />
          </div>
        );
      } else {
        elements.push(<div key={"code@"+start} className={rv.trim() || undefined}><CodeBlock code={rawCode} lang={lang} fileName={fileHint} index={++codeSeq} /></div>);
      }
      i++;
      continue;
    }

    // Table (baris yang mengandung |)
    if (line.includes('|') && line.trim().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = parseTableRows(tableLines);
      // Key by baris-awal → tabel yang tumbuh (baris nambah) tetap satu instance,
      // jadi hanya <tr> baru yang ter-animasi (lihat MarkdownTable).
      elements.push(<MarkdownTable key={"tbl@"+start} rows={rows} animate={animate} />);
      continue;
    }

    // Heading ###
    if (line.startsWith('### '))
      { elements.push(<h3 key={"h3@"+start} className={"font-bold text-slate-800 text-sm mt-3 mb-1"+rv}>{renderInline(line.slice(4))}</h3>); i++; continue; }
    if (line.startsWith('## '))
      { elements.push(<h2 key={"h2@"+start} className={"font-bold text-slate-800 text-base mt-3 mb-1"+rv}>{renderInline(line.slice(3))}</h2>); i++; continue; }
    if (line.startsWith('# '))
      { elements.push(<h1 key={"h1@"+start} className={"font-bold text-slate-800 text-lg mt-3 mb-1"+rv}>{renderInline(line.slice(2))}</h1>); i++; continue; }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line.trim()))
      { elements.push(<hr key={"hr@"+start} className={"my-3 border-slate-200"+rv} />); i++; continue; }

    // Unordered list
    if (/^[-*+] /.test(line)) {
      const listItems = [];
      while (i < lines.length && /^[-*+] /.test(lines[i])) {
        listItems.push(lines[i].replace(/^[-*+] /, ''));
        i++;
      }
      elements.push(
        <ul key={"ul@"+start} className={"my-1.5 space-y-1 ml-1"+rv}>
          {listItems.map((item, j) => (
            <li key={j} className="flex gap-2 text-sm text-slate-700">
              <span className="text-brand-400 mt-1 flex-shrink-0">•</span>
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(line)) {
      const listItems = [];
      let num = 1;
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        listItems.push({ n: num++, text: lines[i].replace(/^\d+\. /, '') });
        i++;
      }
      elements.push(
        <ol key={"ol@"+start} className={"my-1.5 space-y-1 ml-1"+rv}>
          {listItems.map((item, j) => (
            <li key={j} className="flex gap-2 text-sm text-slate-700">
              <span className="text-brand-500 font-semibold flex-shrink-0 w-4">{item.n}.</span>
              <span>{renderInline(item.text)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={"bq@"+start} className={"my-2 pl-3 border-l-2 border-brand-300 text-sm text-slate-600 italic"+rv}>
          {renderInline(line.slice(2))}
        </blockquote>
      );
      i++; continue;
    }

    // Empty line
    if (line.trim() === '') { elements.push(<div key={"sp@"+start} className="h-1.5" />); i++; continue; }

    // Normal paragraph
    elements.push(
      <p key={"p@"+start} className={"text-sm text-slate-700 leading-relaxed"+rv}>{renderInline(line)}</p>
    );
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}
